import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SEO from '../../components/SEO';
import { useTranslation } from 'react-i18next';
import {
  Trophy,
  Target,
  Flame,
  BookOpen,
  Lock,
  Unlock,
  ArrowRight,
  History,
  Activity,
  Layers,
  ClipboardList,
  AlertCircle,
  GraduationCap,
  Medal,
  Star,
  Zap,
  Headphones
} from 'lucide-react';
import { encodeId } from '../../utils/ids';
import { getWrongQuestionsCount } from '../../utils/wrongQuestions';
import { BADGES } from '../../utils/badges';
import {
  getUserPlanType,
  hasDetailedStatsAccess,
  canAccessCategory,
  FREE_PLAN_LIMITS
} from '../../utils/subscription';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState<any>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<any>(null);
  const [userProgress, setUserProgress] = useState<any[]>([]);
  const [categoryCounts, setCategoryCounts] = useState<any>({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isPollingSubscription, setIsPollingSubscription] = useState(false);

  const checkAuth = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      window.REACT_APP_NAVIGATE('/login');
      return;
    }
    setUser(user);
    await Promise.all([
      fetchSubscription(user.id),
      loadCategories(),
      loadUserProgress(user.id)
    ]);
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();

    const { data: { subscription: authListener } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (session?.user) {
          setUser(session.user);
          if (event === 'USER_UPDATED') {
            loadUserProgress(session.user.id);
          }
        }
      }
    );

    return () => {
      authListener.unsubscribe();
    };
  }, [checkAuth]);

  // Handle successful payment return from Stripe
  useEffect(() => {
    const success = searchParams.get('success');
    const sessionId = searchParams.get('session_id');

    if (success === 'true' && sessionId && user?.id) {
      // Clear the URL params to prevent re-triggering
      setSearchParams({}, { replace: true });

      // Start polling for the updated subscription
      setIsPollingSubscription(true);

      const pollSubscription = async () => {
        const maxAttempts = 10;
        const pollInterval = 1500; // 1.5 seconds

        console.log('🔄 Starting subscription polling for user:', user.id);

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          // Wait before polling (except first attempt)
          if (attempt > 0) {
            await new Promise(resolve => setTimeout(resolve, pollInterval));
          }

          console.log(`📡 Polling attempt ${attempt + 1}/${maxAttempts}`);

          const { data, error } = await supabase
            .from('subscriptions')
            .select('*')
            .eq('user_id', user.id)
            .eq('status', 'active')
            .single();

          console.log('📦 Polling result:', { data, error });

          if (data && !error) {
            // Subscription found! Update the state
            console.log('✅ Subscription found:', data);
            setSubscription(data);
            setShowSuccessMessage(true);
            setIsPollingSubscription(false);

            // Hide success message after 5 seconds
            setTimeout(() => setShowSuccessMessage(false), 5000);
            return;
          }
        }

        // If we get here, subscription wasn't found after all attempts
        console.log('⚠️ Subscription not found after polling, refreshing...');
        // Still show success message as Stripe payment was completed
        setShowSuccessMessage(true);
        setIsPollingSubscription(false);
        setTimeout(() => setShowSuccessMessage(false), 5000);

        // Refresh subscription one more time
        await fetchSubscription(user.id);
      };

      pollSubscription();
    }
  }, [searchParams, user?.id, setSearchParams]);

  const fetchSubscription = async (userId: string) => {
    try {
      console.log('🔍 Fetching subscription for user:', userId);
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .single();

      console.log('📦 Subscription fetch result:', { data, error });

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching subscription:', error);
      }

      setSubscription(data);
      console.log('✅ Subscription state set to:', data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('id', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
      await loadCategoryCounts(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
    }
  };

  const loadCategoryCounts = async (cats: any[]) => {
    const counts: any = {};
    for (const cat of cats) {
      const [flashcardsData, qcmData, vraiFauxData] = await Promise.all([
        supabase.from('flashcards').select('id', { count: 'exact', head: true }).eq('category_id', cat.id),
        supabase.from('qcm').select('id', { count: 'exact', head: true }).eq('category_id', cat.id),
        supabase.from('vrai_faux').select('id', { count: 'exact', head: true }).eq('category_id', cat.id)
      ]);

      counts[cat.id] = {
        flashcards: flashcardsData.count || 0,
        qcm: qcmData.count || 0,
        vrai_faux: vraiFauxData.count || 0,
        total: (flashcardsData.count || 0) + (qcmData.count || 0) + (vraiFauxData.count || 0)
      };
    }
    setCategoryCounts(counts);
  };

  const loadUserProgress = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', userId);

      if (error) throw error;
      setUserProgress(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement de la progression:', error);
    }
  };

  const getCategoryProgress = (categoryId: number) => {
    const progress = userProgress.find(p => p.category_id === categoryId);
    let counts = categoryCounts[categoryId] || { qcm: 0, vrai_faux: 0, flashcards: 0, total: 1 };

    const planType = getUserPlanType(subscription);
    if (planType === 'free') {
      counts = {
        ...counts,
        flashcards: Math.min(counts.flashcards, FREE_PLAN_LIMITS.maxFlashcards),
        qcm: Math.min(counts.qcm, FREE_PLAN_LIMITS.maxQuiz),
        vrai_faux: Math.min(counts.vrai_faux, FREE_PLAN_LIMITS.maxQuiz),
      };
    }

    if (!progress) {
      const quizTotal = planType === 'free' ? FREE_PLAN_LIMITS.totalQuizLimit : (counts.qcm || 0) + (counts.vrai_faux || 0);
      return {
        progress: 0,
        completed: 0,
        total: quizTotal,
        fcCompleted: 0,
        fcTotal: counts.flashcards || 0,
        qcmCompleted: 0,
        vfCompleted: 0
      };
    }

    const quizCompleted = (progress.qcm_completed || 0) + (progress.vrai_faux_completed || 0);
    const quizTotal = planType === 'free' ? FREE_PLAN_LIMITS.totalQuizLimit : (counts.qcm || 0) + (counts.vrai_faux || 0);
    const progressPercent = quizTotal > 0 ? Math.min(Math.round((quizCompleted / quizTotal) * 100), 100) : 0;

    return {
      progress: progressPercent,
      completed: Math.min(quizCompleted, quizTotal),
      total: quizTotal,
      fcCompleted: Math.min(progress.flashcards_completed || 0, counts.flashcards),
      fcTotal: counts.flashcards || 0,
      qcmCompleted: progress.qcm_completed || 0,
      vfCompleted: progress.vrai_faux_completed || 0
    };
  };

  const getTotalStats = () => {
    const planType = getUserPlanType(subscription);

    const quizCompleted = userProgress.reduce((sum, p) => {
      const pQuizCompleted = (p.qcm_completed || 0) + (p.vrai_faux_completed || 0);
      if (planType === 'free') {
        return sum + Math.min(pQuizCompleted, FREE_PLAN_LIMITS.totalQuizLimit);
      }
      return sum + pQuizCompleted;
    }, 0);

    const quizTotal = Object.entries(categoryCounts).reduce((sum: number, [catId, count]: [string, any]) => {
      const categoryIndex = categories.findIndex(c => c.id === parseInt(catId));
      if (planType === 'free') {
        if (canAccessCategory(subscription, categoryIndex)) {
          return sum + FREE_PLAN_LIMITS.totalQuizLimit;
        }
        return sum;
      }
      return sum + (count.qcm || 0) + (count.vrai_faux || 0);
    }, 0);

    const totalCompleted = userProgress.reduce((sum, p) => {
      const pTotal = (p.flashcards_completed || 0) + (p.qcm_completed || 0) + (p.vrai_faux_completed || 0);
      if (planType === 'free') {
        return sum + Math.min(pTotal, FREE_PLAN_LIMITS.maxFlashcards + FREE_PLAN_LIMITS.maxQuiz);
      }
      return sum + pTotal;
    }, 0);

    const totalCorrect = userProgress.reduce((sum, p) => sum + (p.total_score || 0), 0);

    return {
      quizTotal,
      quizCompleted,
      totalCompleted,
      correctAnswers: totalCorrect,
      streak: user?.user_metadata?.streak_count || 0,
      wrongQuestions: getWrongQuestionsCount(user)
    };
  };

  const planType = getUserPlanType(subscription);
  const isPremium = planType === 'premium';
  const hasStatsAccess = hasDetailedStatsAccess(subscription);

  const statsData = getTotalStats();
  const progressPercentage = (statsData.quizTotal as number) > 0 ? Math.round(((statsData.quizCompleted as number) / (statsData.quizTotal as number)) * 100) : 0;
  const accuracyPercentage = (statsData.totalCompleted as number) > 0 ? Math.round(((statsData.correctAnswers as number) / (statsData.totalCompleted as number)) * 100) : 100;

  const getTimeAgo = (date: Date) => {
    const diffInSeconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (diffInSeconds < 60) return t('dashboard.activity.time_just_now');
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return t('dashboard.activity.time_min', { count: diffInMinutes });
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return t('dashboard.activity.time_hours', { count: diffInHours });
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return t('dashboard.activity.time_yesterday');
    return t('dashboard.activity.time_days', { count: diffInDays });
  };

  const recentActivities = [
    ...(user?.user_metadata?.activity_log || []).map((a: any) => ({ ...a, date: new Date(a.date) })),
    ...userProgress
      .filter(p => p.last_activity && !(user?.user_metadata?.activity_log || []).some((a: any) => a.categoryId === p.category_id))
      .flatMap(p => {
        const category = categories.find(c => c.id === p.category_id);
        const title = (i18n.language === 'en' ? category?.title_en : category?.title) || category?.title;
        const acts = [];
        if ((p.qcm_completed || 0) + (p.vrai_faux_completed || 0) > 0) {
          acts.push({
            id: `fb-quiz-${p.id}`,
            type: 'quiz',
            title: title || 'Quiz',
            subtitle: t('dashboard.activity.empty'), // Using a placeholder for "In progress" or similar if needed
            date: new Date(p.last_activity),
            value: (p.qcm_completed || 0) + (p.vrai_faux_completed || 0)
          });
        }
        if ((p.flashcards_completed || 0) > 0) {
          acts.push({
            id: `fb-fc-${p.id}`,
            type: 'flashcards',
            title: title || 'Flashcards',
            subtitle: t('dashboard.activity.empty'),
            date: new Date(p.last_activity),
            value: p.flashcards_completed
          });
        }
        return acts;
      })
  ]
    .sort((a, b) => b.date.getTime() - a.date.getTime())
    .slice(0, 3);

  if (loading || isPollingSubscription) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-muted">
            {isPollingSubscription ? t('dashboard.payment.processing') : t('pricing.status.loading')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout user={user}>
      <SEO noindex={true} />
      {/* Success Payment Message */}
      {showSuccessMessage && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-top fade-in duration-300">
          <div className="bg-green-500/10 border border-green-500/30 backdrop-blur-xl rounded-2xl px-6 py-4 flex items-center gap-4 shadow-2xl">
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
              <Trophy className="text-white" size={20} />
            </div>
            <div>
              <p className="text-green-500 font-bold text-lg">{t('dashboard.payment.success_title')}</p>
              <p className="text-text-muted text-sm">{t('dashboard.payment.success_message')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Section */}
      <div className="mb-8 relative overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-text-main mb-2 tracking-tight">
              {t('dashboard.welcome.title', { name: user?.user_metadata?.full_name || 'Citoyen' })}
            </h1>
            <p className="text-text-muted text-lg max-w-2xl">
              {t('dashboard.welcome.description')}
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm font-medium text-text-muted px-4 py-2 bg-dark-card rounded-full border border-dark-border">
            <Activity size={16} className="text-primary" />
            {t('dashboard.welcome.last_activity', { time: recentActivities[0] ? getTimeAgo(recentActivities[0].date) : t('dashboard.welcome.never') })}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: t('dashboard.stats.completed_quiz'), value: `${statsData.quizCompleted}/${statsData.quizTotal}`, icon: BookOpen },
          { label: t('dashboard.stats.progression'), value: `${progressPercentage}%`, icon: Target },
          { label: t('dashboard.stats.accuracy'), value: `${accuracyPercentage}%`, icon: Trophy },
          { label: t('dashboard.stats.to_review'), value: statsData.wrongQuestions, icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-500/10' },
        ].map((stat, i) => (
          <div key={i} className="bg-dark-card p-5 rounded-2xl flex items-center gap-4 hover:border-primary/50 transition-all border border-dark-border group">
            <div className={`p-3 rounded-xl ${stat.bg || 'bg-primary/10'} ${stat.color || 'text-primary'} group-hover:scale-110 transition-transform`}>
              <stat.icon size={20} />
            </div>
            <div className="min-w-0">
              <div className="text-xl font-black text-text-main truncate">{stat.value as React.ReactNode}</div>
              <div className="text-[11px] font-bold text-text-muted uppercase tracking-wider truncate">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Progress Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 items-stretch">
        <div className="lg:col-span-2 flex flex-col gap-6">
          <div className="bg-dark-card p-6 rounded-3xl border border-dark-border flex flex-col h-full">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-black text-text-main flex items-center gap-2">
                <Target className="text-primary" size={24} />
                {t('dashboard.main.progression')}
              </h2>
              {hasStatsAccess && (
                <Link to="/stats" className="text-primary hover:text-primary-hover text-xs font-bold uppercase tracking-wider">
                  {t('dashboard.main.details')}
                </Link>
              )}
            </div>

            <div className="flex-1 flex flex-col justify-center">
              <div className="mb-8 p-8 bg-dark-bg/40 rounded-[2rem] border border-dark-border/50 relative overflow-hidden group">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-primary/5 rounded-full blur-[80px] pointer-events-none" />

                <div className="flex flex-col md:flex-row items-center gap-10 relative z-10">
                  <div className="relative w-44 h-44 flex-shrink-0">
                    <svg className="w-full h-full transform -rotate-90 drop-shadow-[0_0_15px_rgba(220,38,38,0.25)]" viewBox="0 0 176 176">
                      <circle cx="88" cy="88" r="80" stroke="currentColor" strokeWidth="12" fill="transparent" className="text-dark-card/50" />
                      <circle cx="88" cy="88" r="80" stroke="url(#progressGradientDashboard)" strokeWidth="12" strokeDasharray={2 * Math.PI * 80} strokeDashoffset={2 * Math.PI * 80 * (1 - progressPercentage / 100)} strokeLinecap="round" fill="transparent" className="transition-all duration-[1500ms] ease-out-expo" />
                      <defs>
                        <linearGradient id="progressGradientDashboard" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="var(--color-primary, #DC2626)" />
                          <stop offset="100%" stopColor="#ef4444" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-5xl font-black text-text-main tracking-tighter">{progressPercentage}%</span>
                      <span className="text-[10px] text-text-muted font-black uppercase tracking-widest mt-1">{t('dashboard.main.mastery')}</span>
                    </div>
                  </div>

                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-2xl font-black text-text-main mb-3">{t('dashboard.main.level')}</h3>
                    <p className="text-text-muted text-sm leading-relaxed mb-6">
                      {t('dashboard.main.cta_continue')}
                    </p>
                    <div className="flex flex-wrap justify-center md:justify-start gap-6">
                      <div className="flex items-center gap-2.5">
                        <div className="w-3 h-3 rounded-full bg-primary/20 border border-primary/40" />
                        <span className="text-xs text-text-muted font-bold uppercase tracking-wider">
                          {progressPercentage < 33 ? t('dashboard.main.level_beginner') : progressPercentage < 66 ? t('dashboard.main.level_intermediate') : t('dashboard.main.level_advanced')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/40" />
                        <span className="text-xs text-text-muted font-bold uppercase tracking-wider">
                          {statsData.quizCompleted} Quiz
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {[
                  { value: statsData.totalCompleted, label: t('dashboard.main.total_answers'), color: 'text-text-main' },
                  { value: statsData.correctAnswers, label: t('dashboard.main.correct_answers'), color: 'text-primary' },
                  { value: statsData.streak, label: t('dashboard.main.current_streak'), color: 'text-orange-500' }
                ].map((s, idx) => (
                  <div key={idx} className="p-4 bg-dark-bg/50 rounded-2xl border border-dark-border text-center group hover:border-primary/30 transition-all font-mono">
                    <div className={`text-2xl font-black ${s.color} mb-0.5 group-hover:scale-110 transition-transform`}>{s.value}</div>
                    <div className="text-[9px] text-text-muted font-black uppercase tracking-widest">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-dark-card p-6 rounded-3xl border border-dark-border">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-text-main flex items-center gap-2">
                <Medal className="text-primary" size={24} />
                {t('dashboard.badges.title')}
              </h2>
              <span className="text-xs font-bold text-text-muted bg-dark-bg px-3 py-1 rounded-full border border-dark-border">
                {user?.user_metadata?.badges?.length || 0} {
                  (user?.user_metadata?.badges?.length || 0) > 1
                    ? t('dashboard.badges.count_plural', { count: user?.user_metadata?.badges?.length })
                    : t('dashboard.badges.count', { count: user?.user_metadata?.badges?.length || 0 })
                }
              </span>
            </div>

            <div className="flex flex-wrap gap-4">
              {user?.user_metadata?.badges?.length > 0 ? (
                user.user_metadata.badges.map((badge: any) => {
                  const staticBadge = BADGES.find(b => b.title === badge.title);
                  const imageSrc = badge.image || staticBadge?.image;
                  const title = (i18n.language === 'en' ? (badge.title_en || staticBadge?.title_en || badge.title) : (badge.title)) || badge.title;
                  const description = (i18n.language === 'en' ? (badge.description_en || staticBadge?.description_en || badge.description) : (badge.description)) || t('stats.badges.congrats');

                  return (
                    <div key={badge.id} className="group relative flex flex-col items-center gap-2 p-4 bg-dark-bg/50 rounded-2xl border border-dark-border hover:border-primary/30 transition-all cursor-help shrink-0">
                      {imageSrc ? (
                        <div className="w-14 h-14 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <img src={imageSrc} alt={title} className="w-full h-full object-contain drop-shadow-md rounded-full" />
                        </div>
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                          {badge.icon === 'Trophy' && <Trophy size={28} />}
                          {badge.icon === 'Star' && <Star size={28} />}
                          {badge.icon === 'Zap' && <Zap size={28} />}
                          {badge.icon === 'GraduationCap' && <GraduationCap size={28} />}
                        </div>
                      )}
                      <span className="text-[10px] font-black uppercase tracking-widest text-text-muted group-hover:text-text-main transition-colors text-center">{title}</span>
                      <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 w-48 bg-dark-card border border-dark-border p-2 rounded-lg text-[10px] text-text-muted opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-20 text-center shadow-2xl">
                        {description}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="flex flex-col items-center justify-center w-full py-6 text-center border-2 border-dashed border-dark-border rounded-3xl bg-dark-bg/20">
                  <div className="w-12 h-12 bg-dark-card rounded-full flex items-center justify-center text-dark-border mb-3">
                    <Medal size={24} />
                  </div>
                  <p className="text-xs text-text-muted max-w-xs mx-auto font-medium">
                    {t('dashboard.badges.empty')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          {!isPremium && (
            <div className="bg-gradient-to-br from-dark-card to-dark-bg p-6 rounded-2xl border border-dark-border relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full -translate-y-16 translate-x-16 blur-3xl group-hover:bg-primary/20 transition-all font-bold" />
              <div className="relative z-10">
                <div className="w-12 h-12 bg-primary text-white rounded-xl flex items-center justify-center mb-4 font-bold shadow-lg shadow-primary/20">
                  <Trophy size={24} />
                </div>
                <h3 className="text-xl font-extrabold text-text-main mb-2">{t('dashboard.premium.title')}</h3>
                <p className="text-text-muted text-sm mb-5 leading-relaxed">
                  {t('dashboard.premium.description')}
                </p>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/pricing')}
                  className="w-full py-3 bg-primary hover:bg-primary-hover text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-primary/20 active:scale-95 translate-y-0"
                >
                  {t('dashboard.premium.cta')}
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}

          <div className="bg-dark-card p-6 rounded-2xl border border-dark-border group hover:border-primary/50 transition-all">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                <GraduationCap size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-text-main">{t('dashboard.exam.title')}</h3>
                <p className="text-xs text-text-muted">{t('dashboard.exam.subtitle')}</p>
              </div>
            </div>
            <p className="text-sm text-text-muted mb-4 leading-relaxed">
              {t('dashboard.exam.description')}
            </p>
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/exam')}
              className="w-full py-2.5 bg-dark-bg hover:bg-dark-hover text-text-main font-bold rounded-xl border border-dark-border transition-all flex items-center justify-center gap-2 group-hover:border-primary/30"
            >
              {t('dashboard.exam.cta')}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          <div className={`bg-dark-card p-6 rounded-3xl border border-dark-border flex flex-col ${isPremium ? 'h-full' : 'flex-1'}`}>
            <div className="flex items-center gap-2 mb-5">
              <History size={20} className="text-primary" />
              <h2 className="text-lg font-bold text-text-main">{t('dashboard.activity.title')}</h2>
            </div>

            <div className="space-y-4">
              {recentActivities.length > 0 ? (
                recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3 group">
                    <div className="w-8 h-8 rounded-lg bg-dark-bg border border-dark-border flex items-center justify-center shrink-0 mt-0.5 group-hover:border-primary/30 transition-colors">
                      {activity.type === 'audio' ? (
                        <Headphones size={14} className="text-blue-500/60 group-hover:text-blue-500 transition-colors" />
                      ) : activity.type === 'flashcards' ? (
                        <Layers size={14} className="text-orange-500/60 group-hover:text-orange-500 transition-colors" />
                      ) : (
                        <ClipboardList size={14} className="text-primary/60 group-hover:text-primary transition-colors" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex justify-between items-start gap-2">
                        <p className="text-sm font-bold text-text-main truncate capitalize">{activity.title}</p>
                        <span className="text-[10px] text-text-muted font-medium shrink-0">{getTimeAgo(activity.date)}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-[10px] text-text-muted font-medium capitalize">{activity.subtitle}</p>
                        {activity.type === 'quiz' && activity.value !== null && (
                          <>
                            <span className="w-1 h-1 rounded-full bg-dark-border" />
                            <span className="text-[10px] text-primary font-bold">{activity.value} pts</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center py-8">
                  <Activity size={32} className="text-dark-border mb-3" />
                  <p className="text-xs text-text-muted">{t('dashboard.activity.empty')}</p>
                </div>
              )}
            </div>

            {recentActivities.length > 0 && (
              <Link to="/stats" className="mt-6 pt-6 border-t border-dark-border text-xs font-bold text-primary hover:text-primary-hover flex items-center justify-center gap-2">
                {t('dashboard.activity.view_all')}
                <ArrowRight size={14} />
              </Link>
            )}
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-xl font-bold text-text-main mb-4">{t('dashboard.learning.title')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category, index) => {
            const progress = getCategoryProgress(category.id);
            const isLocked = !canAccessCategory(subscription, index);
            const title = (i18n.language === 'en' ? category.title_en : category.title) || category.title;
            const description = (i18n.language === 'en' ? category.description_en : category.description) || category.description;

            return (
              <div key={category.id} className={`bg-dark-card rounded-2xl border border-dark-border overflow-hidden transition-all ${isLocked ? 'opacity-70' : 'hover:border-primary/50'}`}>
                <div className="h-32 bg-dark-bg relative flex items-center justify-center border-b border-dark-border">
                  <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white to-transparent" />

                  {isLocked ? (
                    <Lock size={40} className="text-text-muted" />
                  ) : (
                    <div className="text-primary font-bold text-5xl opacity-20">
                      {index + 1}
                    </div>
                  )}

                  <div className="absolute bottom-4 right-4">
                    {isLocked && <span className="px-2 py-0.5 bg-red-500/10 text-red-600 text-[10px] font-bold rounded border border-red-500/20 shadow-sm">{t('dashboard.learning.pro')}</span>}
                  </div>
                </div>

                <div className="p-5">
                  <h3 className="text-lg font-bold text-text-main mb-1.5">{title}</h3>
                  <p className="text-text-muted text-sm mb-4 line-clamp-2">{description}</p>

                  {!isLocked && (
                    <div className="mb-5">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-text-muted">{t('dashboard.learning.progression')}</span>
                        <span className="text-text-main">{progress.progress}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-dark-bg rounded-full overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: `${progress.progress}%` }} />
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    {isLocked ? (
                      <button
                        onClick={() => window.REACT_APP_NAVIGATE('/pricing')}
                        className="w-full py-2.5 border border-dark-border rounded-xl text-text-muted text-sm font-medium hover:bg-dark-hover flex items-center justify-center gap-2"
                      >
                        <Unlock size={16} /> {t('dashboard.learning.unlock')}
                      </button>
                    ) : (
                      <>
                        {getUserPlanType(subscription) === 'free' && progress.fcCompleted >= FREE_PLAN_LIMITS.maxFlashcards ? (
                          <button
                            onClick={() => window.REACT_APP_NAVIGATE('/pricing')}
                            className="flex-1 py-2 rounded-xl bg-dark-bg border border-red-500/30 text-red-400 text-sm font-medium hover:bg-red-500/5 transition-colors text-center relative group/btn"
                          >
                            {t('dashboard.learning.finished')} <span className="text-[10px] ml-1 opacity-70">{t('dashboard.learning.pro')}</span>
                          </button>
                        ) : (
                          <Link to={`/flashcards?category=${encodeId(category.id)}`} className="flex-1 py-2 rounded-xl bg-dark-bg border border-dark-border text-text-main text-sm font-medium hover:border-primary/50 hover:text-primary transition-colors text-center relative group/btn">
                            {t('dashboard.learning.flashcards')}
                            {progress.fcTotal > 0 && (
                              <span className="absolute -top-2 -right-1 bg-dark-bg border border-dark-border text-[9px] px-1.5 py-0.5 rounded-full text-text-muted group-hover/btn:text-primary transition-colors">
                                {progress.fcCompleted}/{progress.fcTotal}
                              </span>
                            )}
                          </Link>
                        )}

                        {getUserPlanType(subscription) === 'free' && (progress.qcmCompleted >= FREE_PLAN_LIMITS.maxQuiz && progress.vfCompleted >= FREE_PLAN_LIMITS.maxQuiz) ? (
                          <button
                            onClick={() => window.REACT_APP_NAVIGATE('/pricing')}
                            className="flex-1 py-2 rounded-xl bg-dark-bg border border-red-500/30 text-red-400 text-sm font-medium hover:bg-red-500/5 transition-colors text-center"
                          >
                            {t('dashboard.learning.quiz')} <span className="text-[10px] ml-1 opacity-70">{t('dashboard.learning.pro')}</span>
                          </button>
                        ) : (
                          <Link to={`/quiz?category=${encodeId(category.id)}`} className="flex-1 py-2 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary-hover transition-colors text-center">
                            {t('dashboard.learning.quiz')}
                          </Link>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </DashboardLayout >
  );
}
